import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  ClipboardList, 
  Play, 
  Lock, 
  Crown,
  ChevronRight,
  Sparkles,
  AlertCircle
} from "lucide-react";

export function PracticeTestCard({ planType = "starter", onUpgradeClick }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const res = await fetch(`${API}/practice-tests/usage`, {
        credentials: "include"
      });
      const data = await res.json();
      
      if (data.success) {
        setUsage(data);
        setHasAccess(data.can_take);
      }
    } catch (error) {
      console.error("Failed to fetch practice test usage:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPlanLimits = () => {
    // Based on subscription.py config
    const limits = {
      starter: { daily: 1, name: "Starter" },
      plus: { daily: 3, name: "Plus" },
      pro: { daily: "Unlimited", name: "Pro" },
      free_trial: { daily: "Unlimited", name: "Free Trial" }
    };
    return limits[planType] || limits.starter;
  };

  const planLimits = getPlanLimits();
  const isLimitReached = usage && !usage.can_take;
  const testsRemaining = usage?.tests_remaining;
  const testsTaken = usage?.tests_taken || 0;
  const dailyLimit = usage?.daily_limit || planLimits.daily;

  if (loading) {
    return (
      <Card className="border-0 shadow-md animate-pulse">
        <CardContent className="py-6">
          <div className="h-20 bg-gray-200 rounded"></div>
        </CardContent>
      </Card>
    );
  }

  // Locked state for Starter users who hit limit
  if (isLimitReached && planType === "starter") {
    return (
      <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-md">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-amber-700">
            <ClipboardList className="w-5 h-5" />
            Practice Tests
            <Badge variant="outline" className="ml-auto bg-amber-100 text-amber-700 border-amber-300">
              {planLimits.name}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-white/80 rounded-lg border border-amber-200">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-amber-800">Daily Limit Reached</p>
              <p className="text-sm text-amber-600">You've used {testsTaken}/{dailyLimit} test today</p>
            </div>
          </div>
          
          <Button
            onClick={onUpgradeClick}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
            data-testid="upgrade-for-tests-btn"
          >
            <Crown className="w-4 h-4 mr-2" />
            Upgrade for More Tests
          </Button>
          
          <p className="text-xs text-center text-amber-600">
            Plus: 3 tests/day • Pro: Unlimited tests
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-md hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-blue-50 to-indigo-50">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-blue-700">
          <ClipboardList className="w-5 h-5" />
          Practice Tests
          <Badge variant="outline" className="ml-auto bg-blue-100 text-blue-700 border-blue-200">
            {planLimits.name}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Usage Display */}
        <div className="flex items-center justify-between p-3 bg-white/80 rounded-lg">
          <div>
            <p className="text-sm text-gray-600">Today's Tests</p>
            <p className="text-2xl font-bold text-blue-700">
              {testsTaken}
              <span className="text-sm font-normal text-gray-500">
                /{dailyLimit === "unlimited" ? "∞" : dailyLimit}
              </span>
            </p>
          </div>
          {dailyLimit !== "unlimited" && (
            <div className="w-16 h-16">
              <Progress 
                value={(testsTaken / dailyLimit) * 100} 
                className="h-2 bg-blue-100"
              />
              <p className="text-xs text-center mt-1 text-gray-500">
                {testsRemaining === "unlimited" ? "∞" : testsRemaining} left
              </p>
            </div>
          )}
        </div>

        {/* Start Test Button */}
        <Button
          onClick={() => navigate("/student/practice-tests")}
          disabled={isLimitReached}
          className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white"
          data-testid="start-practice-test-btn"
        >
          <Play className="w-4 h-4 mr-2" />
          {isLimitReached ? "Limit Reached" : "Start Practice Test"}
          <ChevronRight className="w-4 h-4 ml-auto" />
        </Button>

        {/* Plan Benefits */}
        {planType === "starter" && (
          <p className="text-xs text-center text-gray-500">
            <Sparkles className="w-3 h-3 inline mr-1" />
            Upgrade to Plus for 3 tests/day
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default PracticeTestCard;
